import { endOfMonth, startOfMonth } from 'date-fns'
import { createMonthlyCache } from '../../../../helpers/cache-statistics'
import { firestore } from '../../../../services/firebase'
import { graphql } from '../../../../services/graphql'

export type EventNap = {
  _id: string
  time: { start: string; end: string }
  space: string
  cocoon?: { _id: string; room?: { _id: string } }
  user: string
  userInfo?: { email: string; _id: string }
  evaluation?: {
    rating: number
    comment: string
  }
}
export type EventUserSpace = {
  space: string
  join: string
  leave?: string
  user: string
}
export type EventAudioPlay = {
  space: string
  user: string
  napId?: string
  theme?: string
  book?: string
  bookChapter?: number
  background: string
  duration: number
  date: string
}
export type EventNoSlotWarning = { space: string; date: string }

export type EventRating = {
  configuration: {
    background: string
    theme?: string
    book?: string
  }
  space: string
  date: string
  message: string
  rating: number
  user: {
    email: string
    canBeContacted: boolean
  }
}

const aggregateMonthParts = async (type: string, month: string) => {
  const parts = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

  const mainDocument = firestore()
    .doc(`/offline-first/offline-first/${type}/${month}`)
    .get()
  const partDocuments = parts.map((part) =>
    firestore()
      .doc(`/offline-first/offline-first/${type}/${month}/parts/part${part}`)
      .get(),
  )

  return Promise.all([mainDocument, ...partDocuments])
}

export const feedbackCache = createMonthlyCache(
  (month: string) =>
    firestore()
      .collection('/offline-first/offline-first/feedback')
      .where('date', '>=', startOfMonth(new Date(month + '-01')).toISOString())
      .where('date', '<=', endOfMonth(new Date(month + '-01')).toISOString())
      .get()
      .then((res) =>
        res.docs.map(
          (doc) =>
            doc.data() as {
              category: 'audio' | 'application'
              date: string
              message: string
              user: { email: string }
            },
        ),
      ),
  (months) => months.flat(),
)

export const ratingsCache = createMonthlyCache(
  (month: string) =>
    aggregateMonthParts('ratings', month).then((parts) =>
      parts.flatMap((doc) => Object.values(doc.data() ?? {})),
    ),
  (months) => months.flat(),
)

export const eventsCache = createMonthlyCache(
  (month: string) => {
    return aggregateMonthParts('events', month).then((parts) => ({
      audioPlay: parts.flatMap(
        (doc) => (doc.data()?.['audio-play'] ?? []) as EventAudioPlay[],
      ),
      noSlotWarnings: parts.flatMap(
        (doc) =>
          (doc.data()?.['no-slot-warning'] ?? []) as EventNoSlotWarning[],
      ),
    }))
  },
  (months) => {
    return {
      audioPlay: months.flatMap((m) => m.audioPlay),
      noSlotWarnings: months.flatMap((m) => m.noSlotWarnings),
    }
  },
)

const napCachesBySpace = new Map<
  string,
  ReturnType<typeof createMonthlyCache<EventNap[]>>
>()
export function getNapCache(space: string) {
  let cache = napCachesBySpace.get(space)
  if (!cache) {
    cache = createMonthlyCache<EventNap[]>(
      async (month: string) =>
        await loadNaps(
          space,
          startOfMonth(new Date(month + '-01')),
          endOfMonth(new Date(month + '-01')),
        ),
      (months) => months.flat(),
    )
    napCachesBySpace.set(space, cache)
  }
  return cache
}

export async function loadNaps(
  space: string,
  gte: Date,
  lte: Date,
  withDetails?: boolean,
) {
  return (await graphql.queries.getNaps(space, gte, lte, withDetails)).napList
}

// userSpace must reflect every member who joined up to the end of the period
// (not just those who joined within it), otherwise "total users" undercounts
// and can fall below "active users". Cache the full membership per (space, lte).
const userSpaceCachesBySpace = new Map<
  string,
  Map<string, Promise<EventUserSpace[]>>
>()
export function getUserSpaceUpTo(space: string, lte: Date) {
  let byDate = userSpaceCachesBySpace.get(space)
  if (!byDate) {
    byDate = new Map()
    userSpaceCachesBySpace.set(space, byDate)
  }
  const key = lte.toISOString()
  let promise = byDate.get(key)
  if (!promise) {
    promise = loadUserSpace(space, lte)
    promise.catch(() => byDate?.delete(key))
    byDate.set(key, promise)
  }
  return promise
}

const CHUNK_SIZE = 2500
// Pages are fetched in parallel waves: each one costs about a round trip
// regardless of its size, and the all-spaces view needs ~17 of them, which is
// several seconds when chained one after the other. The API exposes no count
// for userSpace, so we widen until a wave comes back short. Measured against
// production (42k rows, 17 pages): 6 -> 3 waves, 9 -> 2 waves and no wasted
// request, 12 -> 2 waves but 6 empty queries for a marginal gain.
const CHUNK_CONCURRENCY = 9

export async function loadUserSpace(space: string, lte: Date) {
  const userSpaces: EventUserSpace[] = []
  let offset = 0

  for (;;) {
    const wave = await Promise.all(
      Array.from({ length: CHUNK_CONCURRENCY }, (_, i) =>
        graphql.queries
          .getUserSpace(space, lte, offset + i * CHUNK_SIZE, CHUNK_SIZE)
          .then((res) => res.userSpaceList),
      ),
    )

    for (const page of wave) {
      userSpaces.push(...page)
    }

    // A short page means the end of the collection: anything the wave asked
    // for beyond it came back empty already.
    if (wave.some((page) => page.length < CHUNK_SIZE)) {
      return userSpaces
    }

    offset += CHUNK_CONCURRENCY * CHUNK_SIZE
  }
}
